import { TypedClaimsResolver_Hackthenorth2026_Sponsor_Perks_Output } from "src/api/types.generated";
import type { IconName } from "src/shared/components/Icon";
import { IS_PRODUCTION } from "src/shared/utils/env";

import { GetSponsorPerksQuery } from "./SponsorPerksContext/graphql/getSponsorPerks.generated";

// TODO: update this AND getSponsorPerks.graphql when 2027 perks pipeline is created
export const CURRENT_YEAR_SPONSOR_PERKS_FIELD_KEY =
  "fields_HACKTHENORTH2026_SPONSOR_PERKS" as const;

export type SponsorPerksData =
  GetSponsorPerksQuery["claims"][0][typeof CURRENT_YEAR_SPONSOR_PERKS_FIELD_KEY]["answers"];

export enum SponsorPerkType {
  WEBSITE_LOGO = "website_logo",
  WEBSITE_FEATURE = "website_feature",
  NEWSLETTER_OPPORTUNITY = "newsletter_opportunity",
  NEWSLETTER_FEATURE = "newsletter_feature",
  NEWSLETTER_SPOTLIGHT = "newsletter_spotlight",
  TECH_TALK_PROPOSAL = "tech_talk_proposal",
  TECH_TALK_DETAILS = "tech_talk_details",
  API_PRIZE = "api_prize",
  API_WORKSHOP = "api_workshop",
  LIGHTNING_CHALLENGE = "lightning_challenge",
  SPONSOR_AN_ACTIVITY = "sponsor_an_activity",
  SPONSOR_A_SNACK = "sponsor_a_snack",
  SPONSOR_A_MEETUP = "sponsor_a_meetup",
  SELECT_TABLE_LOCATION = "select_table_location",
  SOCIAL_MEDIA_PROMOTION = "social_media_promotion",
  NAME_AN_AREA = "name_an_area",
  WELCOME_BOOKLET_BLURB = "welcome_booklet_blurb",
  SHIPPED_SWAG = "shipped_swag",
  OPENING_CEREMONIES_SLIDE = "opening_ceremonies_slide",
  CLOSING_CEREMONIES_SLIDE = "closing_ceremonies_slide",
  COFFEE_CHATS = "coffee_chats",
  JOB_POSTINGS = "job_postings",
}

export const RELEASED_PERKS_PRODUCTION: SponsorPerkType[] = [
  SponsorPerkType.WEBSITE_LOGO,
  SponsorPerkType.WEBSITE_FEATURE,
  SponsorPerkType.JOB_POSTINGS,
  SponsorPerkType.NEWSLETTER_OPPORTUNITY,
  SponsorPerkType.NEWSLETTER_FEATURE,
  SponsorPerkType.NEWSLETTER_SPOTLIGHT,
  SponsorPerkType.TECH_TALK_PROPOSAL,
  // SponsorPerkType.TECH_TALK_DETAILS,
  SponsorPerkType.API_PRIZE,
  SponsorPerkType.API_WORKSHOP,
  SponsorPerkType.SELECT_TABLE_LOCATION,
  SponsorPerkType.SOCIAL_MEDIA_PROMOTION,
  SponsorPerkType.NAME_AN_AREA,
  SponsorPerkType.WELCOME_BOOKLET_BLURB,
  // SponsorPerkType.SHIPPED_SWAG, // moved to dedicated /sponsor/shipped-swag page
  SponsorPerkType.OPENING_CEREMONIES_SLIDE,
  SponsorPerkType.CLOSING_CEREMONIES_SLIDE,
  SponsorPerkType.COFFEE_CHATS,

  SponsorPerkType.LIGHTNING_CHALLENGE,
  SponsorPerkType.SPONSOR_AN_ACTIVITY,
  // SponsorPerkType.SPONSOR_A_SNACK,
  SponsorPerkType.SPONSOR_A_MEETUP,
];

// releasing all of them so we can test on staging
const RELEASED_PERKS_STAGING: SponsorPerkType[] = [
  SponsorPerkType.WEBSITE_LOGO,
  SponsorPerkType.WEBSITE_FEATURE,
  SponsorPerkType.JOB_POSTINGS,
  SponsorPerkType.NEWSLETTER_OPPORTUNITY,
  SponsorPerkType.NEWSLETTER_FEATURE,
  SponsorPerkType.NEWSLETTER_SPOTLIGHT,
  SponsorPerkType.TECH_TALK_PROPOSAL,
  SponsorPerkType.TECH_TALK_DETAILS,
  SponsorPerkType.API_PRIZE,
  SponsorPerkType.API_WORKSHOP,
  SponsorPerkType.SELECT_TABLE_LOCATION,
  SponsorPerkType.SOCIAL_MEDIA_PROMOTION,
  SponsorPerkType.NAME_AN_AREA,
  SponsorPerkType.WELCOME_BOOKLET_BLURB,
  // SponsorPerkType.SHIPPED_SWAG, // moved to dedicated /sponsor/shipped-swag page
  SponsorPerkType.OPENING_CEREMONIES_SLIDE,
  SponsorPerkType.CLOSING_CEREMONIES_SLIDE,
  SponsorPerkType.COFFEE_CHATS,

  SponsorPerkType.LIGHTNING_CHALLENGE,
  SponsorPerkType.SPONSOR_AN_ACTIVITY,
  SponsorPerkType.SPONSOR_A_SNACK,
  SponsorPerkType.SPONSOR_A_MEETUP,
];

export const RELEASED_PERKS = IS_PRODUCTION
  ? RELEASED_PERKS_PRODUCTION
  : RELEASED_PERKS_STAGING;

export const PERKS_TO_LABELS: Record<SponsorPerkType, string> = {
  [SponsorPerkType.WEBSITE_LOGO]: "Website Logo",
  [SponsorPerkType.WEBSITE_FEATURE]: "Website Feature",
  [SponsorPerkType.NEWSLETTER_OPPORTUNITY]: "Newsletter: Opportunity",
  [SponsorPerkType.NEWSLETTER_FEATURE]: "Newsletter: Feature",
  [SponsorPerkType.NEWSLETTER_SPOTLIGHT]: "Newsletter: Spotlight",
  [SponsorPerkType.TECH_TALK_PROPOSAL]: "Tech Talk: Proposal",
  [SponsorPerkType.TECH_TALK_DETAILS]: "Tech Talk: Time Selection",
  [SponsorPerkType.API_PRIZE]: "API Prize",
  [SponsorPerkType.API_WORKSHOP]: "API Workshop",
  [SponsorPerkType.LIGHTNING_CHALLENGE]: "Lightning Challenge",
  [SponsorPerkType.SPONSOR_AN_ACTIVITY]: "Sponsor an Activity",
  [SponsorPerkType.SPONSOR_A_SNACK]: "Sponsor a Snack",
  [SponsorPerkType.SPONSOR_A_MEETUP]: "Sponsor a Meetup",
  [SponsorPerkType.SELECT_TABLE_LOCATION]: "Select Table Location",
  [SponsorPerkType.SOCIAL_MEDIA_PROMOTION]: "Social Media Promotion",
  [SponsorPerkType.NAME_AN_AREA]: "Name an Area",
  [SponsorPerkType.WELCOME_BOOKLET_BLURB]: "Welcome Booklet Blurb",
  [SponsorPerkType.SHIPPED_SWAG]: "Shipped Swag",
  [SponsorPerkType.OPENING_CEREMONIES_SLIDE]: "Opening Ceremonies Slide",
  [SponsorPerkType.CLOSING_CEREMONIES_SLIDE]: "Closing Ceremonies Slide",
  [SponsorPerkType.COFFEE_CHATS]: "Coffee Chats",
  [SponsorPerkType.JOB_POSTINGS]: "Job Postings",
};

export const PERKS_TO_DUE_DATES: Record<
  SponsorPerkType,
  keyof TypedClaimsResolver_Hackthenorth2026_Sponsor_Perks_Output
> = {
  [SponsorPerkType.WEBSITE_LOGO]: "website_logo_due_date",
  [SponsorPerkType.WEBSITE_FEATURE]: "website_feature_due_date",
  [SponsorPerkType.NEWSLETTER_OPPORTUNITY]: "newsletter_opportunity_due_date",
  [SponsorPerkType.NEWSLETTER_FEATURE]: "newsletter_feature_due_date",
  [SponsorPerkType.NEWSLETTER_SPOTLIGHT]: "newsletter_spotlight_due_date",
  [SponsorPerkType.TECH_TALK_PROPOSAL]: "tech_talk_proposal_due_date",
  [SponsorPerkType.TECH_TALK_DETAILS]: "tech_talk_details_due_date",
  [SponsorPerkType.API_PRIZE]: "api_prize_due_date",
  [SponsorPerkType.API_WORKSHOP]: "api_workshop_due_date",
  [SponsorPerkType.LIGHTNING_CHALLENGE]: "lightning_challenge_due_date",
  [SponsorPerkType.SPONSOR_AN_ACTIVITY]: "sponsor_an_activity_due_date",
  [SponsorPerkType.SPONSOR_A_SNACK]: "sponsor_a_snack_due_date",
  [SponsorPerkType.SPONSOR_A_MEETUP]: "sponsor_a_meetup_due_date",
  [SponsorPerkType.SELECT_TABLE_LOCATION]: "select_table_location_due_date",
  [SponsorPerkType.SOCIAL_MEDIA_PROMOTION]: "social_media_promotion_due_date",
  [SponsorPerkType.NAME_AN_AREA]: "name_an_area_due_date",
  [SponsorPerkType.WELCOME_BOOKLET_BLURB]: "welcome_booklet_blurb_due_date",
  [SponsorPerkType.SHIPPED_SWAG]: "shipped_swag_due_date",
  [SponsorPerkType.OPENING_CEREMONIES_SLIDE]:
    "opening_ceremonies_slide_due_date",
  [SponsorPerkType.CLOSING_CEREMONIES_SLIDE]:
    "closing_ceremonies_slide_due_date",
  [SponsorPerkType.COFFEE_CHATS]: "coffee_chats_due_date",
  [SponsorPerkType.JOB_POSTINGS]: "job_postings_due_date",
};

export const FILE_FIELDS: (keyof TypedClaimsResolver_Hackthenorth2026_Sponsor_Perks_Output)[] =
  [
    "website_logo_file",
    "newsletter_feature_image_file",
    "newsletter_spotlight_image_file",
    "social_media_promotion_logo",
    "opening_ceremonies_slide_file",
    "closing_ceremonies_slide_file",
    "job_postings_logo_file",
  ];

export const DATE_FIELDS: (keyof TypedClaimsResolver_Hackthenorth2026_Sponsor_Perks_Output)[] =
  [];

export const PERK_TO_STATUS_SLUGS: Record<
  SponsorPerkType,
  keyof TypedClaimsResolver_Hackthenorth2026_Sponsor_Perks_Output
> = {
  [SponsorPerkType.WEBSITE_LOGO]: "website_logo_status",
  [SponsorPerkType.WEBSITE_FEATURE]: "website_feature_status",
  [SponsorPerkType.NEWSLETTER_OPPORTUNITY]: "newsletter_opportunity_status",
  [SponsorPerkType.NEWSLETTER_FEATURE]: "newsletter_feature_status",
  [SponsorPerkType.NEWSLETTER_SPOTLIGHT]: "newsletter_spotlight_status",
  [SponsorPerkType.TECH_TALK_PROPOSAL]: "tech_talk_proposal_status",
  [SponsorPerkType.TECH_TALK_DETAILS]: "tech_talk_details_status",
  [SponsorPerkType.API_PRIZE]: "api_prize_status",
  [SponsorPerkType.API_WORKSHOP]: "api_workshop_status",
  [SponsorPerkType.LIGHTNING_CHALLENGE]: "lightning_challenge_status",
  [SponsorPerkType.SPONSOR_AN_ACTIVITY]: "sponsor_an_activity_status",
  [SponsorPerkType.SPONSOR_A_SNACK]: "sponsor_a_snack_status",
  [SponsorPerkType.SPONSOR_A_MEETUP]: "sponsor_a_meetup_status",
  [SponsorPerkType.SELECT_TABLE_LOCATION]: "select_table_location_status",
  [SponsorPerkType.SOCIAL_MEDIA_PROMOTION]: "social_media_promotion_status",
  [SponsorPerkType.NAME_AN_AREA]: "name_an_area_status",
  [SponsorPerkType.WELCOME_BOOKLET_BLURB]: "welcome_booklet_blurb_status",
  [SponsorPerkType.SHIPPED_SWAG]: "shipped_swag_status",
  [SponsorPerkType.OPENING_CEREMONIES_SLIDE]: "opening_ceremonies_slide_status",
  [SponsorPerkType.CLOSING_CEREMONIES_SLIDE]: "closing_ceremonies_slide_status",
  [SponsorPerkType.COFFEE_CHATS]: "coffee_chats_status",
  [SponsorPerkType.JOB_POSTINGS]: "job_postings_status",
};

export enum PerkStatus {
  NOT_STARTED = "Not started",
  INCOMPLETE = "Incomplete",
  SUBMITTED = "Submitted",
  APPROVED = "Approved",
  COMPLETED = "Completed",
}

export const PERK_STATUS_ORDER: PerkStatus[] = [
  PerkStatus.NOT_STARTED,
  PerkStatus.INCOMPLETE,
  PerkStatus.SUBMITTED,
  PerkStatus.APPROVED,
  PerkStatus.COMPLETED,
];

export const PERK_STATUS_TO_LABELS: Record<PerkStatus, string> = {
  [PerkStatus.NOT_STARTED]: "Not started",
  [PerkStatus.INCOMPLETE]: "Incomplete",
  [PerkStatus.SUBMITTED]: "Under Review",
  [PerkStatus.APPROVED]: "Approved",
  [PerkStatus.COMPLETED]: "Completed",
};

export const PERK_STATUS_TO_ICONS: Record<PerkStatus, IconName> = {
  [PerkStatus.NOT_STARTED]: "perk-not-started",
  [PerkStatus.INCOMPLETE]: "perk-in-progress",
  [PerkStatus.SUBMITTED]: "perk-under-review",
  [PerkStatus.APPROVED]: "perk-approved",
  [PerkStatus.COMPLETED]: "perk-completed",
};

export const normalizePerkStatus = (
  status: PerkStatus | null | undefined
): PerkStatus => status ?? PerkStatus.NOT_STARTED;

export const getPerkStatusLabel = (
  status: PerkStatus | null | undefined
): string => PERK_STATUS_TO_LABELS[normalizePerkStatus(status)];

export const getPerkStatusIcon = (
  status: PerkStatus | null | undefined
): IconName => PERK_STATUS_TO_ICONS[normalizePerkStatus(status)];

export const RANK_SELECTION_PERKS: Record<
  SponsorPerkType,
  {
    rankOne: keyof SponsorPerksData;
    rankTwo: keyof SponsorPerksData;
    rankThree?: keyof SponsorPerksData;
  } | null
> = {
  [SponsorPerkType.API_WORKSHOP]: {
    rankOne: "api_workshop_time_one",
    rankTwo: "api_workshop_time_two",
    rankThree: "api_workshop_time_three",
  },
  [SponsorPerkType.TECH_TALK_DETAILS]: {
    rankOne: "tech_talk_details_time_one",
    rankTwo: "tech_talk_details_time_two",
    rankThree: "tech_talk_details_time_three",
  },
  [SponsorPerkType.LIGHTNING_CHALLENGE]: {
    rankOne: "lightning_challenge_time_one",
    rankTwo: "lightning_challenge_time_two",
    rankThree: "lightning_challenge_time_three",
  },
  [SponsorPerkType.SELECT_TABLE_LOCATION]: {
    rankOne: "select_table_location_one",
    rankTwo: "select_table_location_two",
    rankThree: "select_table_location_three",
  },
  [SponsorPerkType.NAME_AN_AREA]: {
    rankOne: "name_an_area_one",
    rankTwo: "name_an_area_two",
  },
  [SponsorPerkType.WEBSITE_LOGO]: null,
  [SponsorPerkType.WEBSITE_FEATURE]: null,
  [SponsorPerkType.NEWSLETTER_OPPORTUNITY]: null,
  [SponsorPerkType.NEWSLETTER_FEATURE]: null,
  [SponsorPerkType.NEWSLETTER_SPOTLIGHT]: null,
  [SponsorPerkType.TECH_TALK_PROPOSAL]: null,
  [SponsorPerkType.API_PRIZE]: null,
  [SponsorPerkType.SPONSOR_AN_ACTIVITY]: null,
  [SponsorPerkType.SPONSOR_A_SNACK]: null,
  [SponsorPerkType.SPONSOR_A_MEETUP]: null,
  [SponsorPerkType.SOCIAL_MEDIA_PROMOTION]: null,
  [SponsorPerkType.WELCOME_BOOKLET_BLURB]: null,
  [SponsorPerkType.SHIPPED_SWAG]: null,
  [SponsorPerkType.OPENING_CEREMONIES_SLIDE]: null,
  [SponsorPerkType.CLOSING_CEREMONIES_SLIDE]: null,
  [SponsorPerkType.COFFEE_CHATS]: null,
  [SponsorPerkType.JOB_POSTINGS]: null,
};
